import React from "react";
import { motion } from "framer-motion";
import { SFI_LOGO, ASIA_AQUATICS_LOGO, KSA_LOGO, INDIVIDUAL_EVENT_COUNT, RELAY_EVENT_COUNT, AGE_CATEGORY_COUNT, COMPETITION_DAYS } from "@/lib/brand";
import { PARTICIPATING_COUNTRIES } from "@/lib/countries";
import { Waves, Award, Calendar, Trophy } from "lucide-react";

/**
 * About section — details of the event + SFI + KSA.
 */
export default function AboutSection() {
  return (
    <section id="about" className="py-16 md:py-24 relative pool-tiles overflow-x-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
        <div className="text-center mb-16">
          <div className="eyebrow mb-4">About the Championship</div>
          <h2 className="font-display font-black text-4xl md:text-5xl leading-tight max-w-3xl mx-auto">
            A celebration of <span className="text-[#00F0FF]">lifelong</span> athletic excellence.
          </h2>
        </div>

        {/* Event blurb */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="card-dark p-5 md:p-10 mb-10"
        >
          <div className="flex items-center gap-3 mb-4">
            <Trophy className="w-6 h-6 text-[#00F0FF]" strokeWidth={1.5} />
          </div>
          <h3 className="font-display font-bold text-2xl md:text-3xl mb-4">
            2nd Asian Masters Swimming Championships 2026
          </h3>
          <div className="space-y-4 text-[#CBD5E1] text-base md:text-lg leading-relaxed">
            <p>
              India hosts the 2nd Asian Masters Swimming Championships from <strong className="text-white">October 4 – 10, 2026</strong> in Bengaluru. Following the inaugural edition, this championship brings together Masters swimmers aged 25 to 80+ from {PARTICIPATING_COUNTRIES.length} nations across Asia, Oceania, and the Middle East.
            </p>
            <p>
              Across <strong className="text-white">{COMPETITION_DAYS} days of competition</strong>, athletes will race in <strong className="text-white">{INDIVIDUAL_EVENT_COUNT} individual events</strong> spanning all four strokes plus Individual Medley, and <strong className="text-white">{RELAY_EVENT_COUNT} relay events</strong>. {AGE_CATEGORY_COUNT} 5-year age bands (25-29 to 80+) ensure every athlete meets peers in their own generation.
            </p>
            <p className="text-[#00F0FF] italic font-semibold">
              "United by Water. Driven by Passion."
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6 pt-6 border-t border-white/10">
            <Stat icon={Calendar} v="Oct 4-10" l="2026" />
            <Stat icon={Waves} v={`${INDIVIDUAL_EVENT_COUNT}+${RELAY_EVENT_COUNT}`} l="Individual / Relays" />
            <Stat icon={Award} v={String(AGE_CATEGORY_COUNT)} l="Age Categories" />
          </div>
        </motion.div>

        {/* SFI + KSA cards */}
        <div className="grid md:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="card-dark p-7"
          >
            <div className="flex items-start gap-4 mb-4">
              <div className="bg-white rounded-lg p-2 shrink-0">
                <img src={SFI_LOGO} alt="SFI" className="h-16 w-auto object-contain" />
              </div>
              <div>
                <div className="text-xs font-semibold text-[#00F0FF] mb-1">Host Federation</div>
                <h3 className="font-display font-bold text-xl">Swimming Federation of India</h3>
                <div className="text-xs text-[#94A3B8] mt-1">Established 1948 · Affiliated to World Aquatics & Asia Aquatics</div>
              </div>
            </div>
            <p className="text-[#CBD5E1] text-sm leading-relaxed">
              The Swimming Federation of India (SFI) is the apex national governing body for swimming, diving, water polo, artistic swimming, and open water swimming in India. Recognised by the Ministry of Youth Affairs & Sports and affiliated to the Indian Olympic Association, SFI conducts national championships, selects India's representation at international events, and is the official host of the 2nd Asian Masters Swimming Championships 2026.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="card-dark p-7"
          >
            <div className="flex items-start gap-4 mb-4">
              <div className="bg-white rounded-lg p-2 shrink-0">
                <img src={KSA_LOGO} alt="KSA" className="h-16 w-16 object-contain" />
              </div>
              <div>
                <div className="text-xs font-semibold text-[#00F0FF] mb-1">Local Organising Committee</div>
                <h3 className="font-display font-bold text-xl">Karnataka Swimming Association</h3>
                <div className="text-xs text-[#94A3B8] mt-1">Bengaluru · State governing body for aquatics</div>
              </div>
            </div>
            <p className="text-[#CBD5E1] text-sm leading-relaxed">
              The Karnataka Swimming Association (KSA) is the state-affiliated body responsible for the promotion, development and governance of swimming in Karnataka. KSA has produced 5 Olympians since the year 2000 and is the recipient of multiple national awards including from Doordarshan. As the local organising committee for AMSC 2026, KSA oversees venue operations at the Centre for Sports Excellence and Dolphin Aquatics — Bengaluru's premier high-performance aquatic facility.
            </p>
          </motion.div>
        </div>

        {/* Sanctioning body */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-6 card-dark p-6 flex flex-col md:flex-row items-center gap-5"
        >
          <div className="bg-white rounded-lg p-2 shrink-0">
            <img src={ASIA_AQUATICS_LOGO} alt="Asia Aquatics" className="h-14 w-auto object-contain" />
          </div>
          <div className="flex-1 text-center md:text-left">
            <div className="text-xs font-semibold text-[#00F0FF] mb-1">Sanctioning Body</div>
            <h3 className="font-display font-bold text-lg">Asia Aquatics</h3>
            <p className="text-[#CBD5E1] text-sm leading-relaxed mt-2">
              The continental aquatic governing body for Asia, affiliated to World Aquatics. Asia Aquatics oversees the Asian Masters Swimming Championships circuit ensuring fair, world-class competition for swimmers of all generations.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

const Stat = ({ icon: Icon, v, l }) => (
  <div className="flex items-center gap-3">
    <div className="w-10 h-10 rounded-full bg-[#00F0FF]/10 flex items-center justify-center">
      <Icon className="w-5 h-5 text-[#00F0FF]" strokeWidth={1.5} />
    </div>
    <div>
      <div className="font-display font-bold text-lg">{v}</div>
      <div className="text-xs text-[#94A3B8] uppercase tracking-wider">{l}</div>
    </div>
  </div>
);
